import { useCallback } from 'react';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useAppTourStore } from '../store/useAppTourStore';
import useMediaQuery from './useMediaQuery';
import { DesktopSteps, MobileSteps } from '../components/AppTour/appTour.config';
import { useAppStore } from '../store/useAppStore';
import { usePostModalStore } from '../store/usePostModalStore';
import { useLocation, useNavigate } from 'react-router-dom';

// Constants for step indices
// Note: These indices reflect the nominal step positions. Joyride automatically skips
// steps when targets are not found (e.g., post cards when no posts exist).
const STEP_INDEX = {
  // Desktop indices
  DESKTOP: {
    DASHBOARD: 0,
    EMPTY_STATE: 1, // Shown when no posts exist (upgrade/no-posts card)
    STORY_CARD: 2,
    FEED_CARD: 3,
    POST_MODAL_PREVIEW: 4,
    POST_MODAL_START: 5,
    POST_MODAL_SCHEDULE: 6,
    POST_MODAL_FEEDBACK: 7,
    POST_MODAL_REGENERATE: 8,
    POST_MODAL_END: 9,
    SOCIAL_ACCOUNTS: 10,
    CONTENT_GALLERY: 11,
    CALENDAR: 12,
    CALENDAR_EVENT: 13,
    CALENDAR_CHANGE_VIEW: 14,
    PROFILE_START: 15,
    PROFILE_SUMMERY: 16,
    PROFILE_SUMMERY_EDIT: 17,
    PROFILE_OFFERINGS: 18,
    PROFILE_OFFERINGS_ADD: 19,
    PROFILE_OFFERINGS_EDIT: 20,
    SETTINGS: 21,
  },
  // Mobile indices (consolidated modal steps)
  MOBILE: {
    DASHBOARD: 0,
    EMPTY_STATE: 1, // Shown when no posts exist (upgrade/no-posts card)
    STORY_CARD: 2,
    FEED_CARD: 3,
    POST_MODAL_PREVIEW: 4,
    POST_MODAL_START: 5,
    SOCIAL_ACCOUNTS: 6,
    CONTENT_GALLERY: 7,
    CALENDAR: 8,
    CALENDAR_EVENT: 9,
    CALENDAR_CHANGE_VIEW: 10,
    PROFILE_START: 11,
    PROFILE_SUMMERY: 12,
    PROFILE_SUMMERY_EDIT: 13,
    PROFILE_OFFERINGS: 14,
    PROFILE_OFFERINGS_ADD: 15,
    PROFILE_OFFERINGS_EDIT: 16,
    SETTINGS: 17,
  },
};

const DELAYS = {
  MODAL_OPEN: 300,
  SIDEBAR_OPEN: 300,
  NAVIGATION: 500, // Extra time for route changes and DOM updates
  DASHBOARD_POLL_INTERVAL: 100, // Interval for polling dashboard elements
  DASHBOARD_MAX_WAIT: 2000, // Max time to wait for dashboard to load
};

// Helper: Get sidebar open steps based on device
const getMobileSidebarOpenSteps = (isMobileView) => {
  const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
  return [
    INDEX.SOCIAL_ACCOUNTS,
    INDEX.CONTENT_GALLERY,
    INDEX.CALENDAR,
    INDEX.DASHBOARD,
    INDEX.PROFILE_START,
  ];
};

// Helper: Get sidebar closed steps based on device
const getMobileSidebarClosedSteps = (isMobileView) => {
  const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
  return [
    INDEX.EMPTY_STATE, // Close sidebar to show no posts container on mobile
    INDEX.STORY_CARD,
    INDEX.FEED_CARD,
    INDEX.POST_MODAL_PREVIEW,
    INDEX.POST_MODAL_START,
    INDEX.CALENDAR_EVENT,
    INDEX.CALENDAR_CHANGE_VIEW,
    INDEX.PROFILE_SUMMERY,
  ];
};

// Helper: Get card scale steps
const getCardScaleSteps = (isMobileView) => {
  const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
  return [INDEX.STORY_CARD, INDEX.FEED_CARD];
};

// Helper: Detect available post cards on dashboard
const detectAvailablePosts = () => {
  const hasStoryCard = !!document.querySelector('.tour-card-for-story');
  const hasFeedCard = !!document.querySelector('.tour-card-for-feed');
  const hasAnyCard = hasStoryCard || hasFeedCard;
  const hasBothCards = hasStoryCard && hasFeedCard;
  return { hasStoryCard, hasFeedCard, hasAnyCard, hasBothCards };
};

// Helper: Detect if calendar has any events
const detectCalendarEvents = () => {
  const hasEvents = !!document.querySelector(
    '.mobile-tour-calendar-event, .desktop-tour-calendar-event'
  );
  return { hasEvents };
};

// Helper: Wait for dashboard to fully load with post detection
const waitForDashboardLoad = () => {
  return new Promise((resolve) => {
    const maxAttempts = DELAYS.DASHBOARD_MAX_WAIT / DELAYS.DASHBOARD_POLL_INTERVAL;
    let attempts = 0;

    const checkDashboard = () => {
      attempts++;

      // Check if post cards OR empty state cards are rendered
      const hasPostCards = !!document.querySelector('.tour-card-for-story, .tour-card-for-feed');
      const hasEmptyState = !!document.querySelector('.tour-no-posts-card, .tour-upgrade-card');
      const isDashboardReady = hasPostCards || hasEmptyState;

      if (isDashboardReady || attempts >= maxAttempts) {
        resolve(detectAvailablePosts());
      } else {
        setTimeout(checkDashboard, DELAYS.DASHBOARD_POLL_INTERVAL);
      }
    };

    checkDashboard();
  });
};

/**
 * Centralized hook for Joyride state and handlers.
 * Relies on persisted app tour store for run/step tracking.
 */
export const useAppTour = () => {
  const { run, stepIndex, startTour, setStepIndex, setRunAndStep, completeTour, tourKey } =
    useAppTourStore();
  const { setSidebarOpen, sidebarOpen } = useAppStore();
  const { isSmallMobile, isMobile, isTablet } = useMediaQuery();
  const { isOpen: isPostModalOpen, closeModal } = usePostModalStore();
  const isMobileView = isSmallMobile || isMobile || isTablet;
  const navigate = useNavigate();
  const location = useLocation();

  // Select steps based on device type (disableBeacon is already set in config)
  const steps = isMobileView ? MobileSteps : DesktopSteps;

  // Helper: Remove scaling from all elements
  const removeScale = () => {
    document.querySelectorAll('.tour-scale-active').forEach((el) => {
      el.classList.remove('tour-scale-active');
    });
  };

  // Helper: Apply scale to card elements
  const applyScaleToElement = (element, index) => {
    const cardScaleSteps = getCardScaleSteps(isMobileView);
    if ((isSmallMobile || isMobile) && cardScaleSteps.includes(index)) {
      element.classList.add('tour-scale-active');
    }
  };

  // Helper: Manage sidebar state based on step
  const updateSidebarForStep = (index) => {
    if (!isMobileView) return;

    const sidebarOpenSteps = getMobileSidebarOpenSteps(isMobileView);
    const sidebarClosedSteps = getMobileSidebarClosedSteps(isMobileView);

    if (sidebarOpen && sidebarClosedSteps.includes(index)) {
      setSidebarOpen(false);
    } else if (!sidebarOpen && sidebarOpenSteps.includes(index)) {
      setSidebarOpen(true);
    }
  };

  // Helper: Perform action with delay and advance step (with smart skipping)
  const delayedStepTransition = (index, action, delay = DELAYS.MODAL_OPEN) => {
    action();
    setTimeout(() => {
      const nextValidIndex = getNextValidStepIndex(index);
      setStepIndex(nextValidIndex);
    }, delay);
    return true; // Prevent default increment
  };

  // Helper: Perform action with delay and jump to specific step
  const delayedStepJump = (targetIndex, action, delay = DELAYS.MODAL_OPEN) => {
    action();
    setTimeout(() => setStepIndex(targetIndex), delay);
    return true; // Prevent default increment
  };

  // Helper: Find next valid step index, skipping unavailable targets
  const getNextValidStepIndex = (currentIndex) => {
    const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
    const { hasAnyCard, hasStoryCard, hasFeedCard } = detectAvailablePosts();
    const { hasEvents } = detectCalendarEvents();

    let nextIndex = currentIndex + 1;

    // Skip empty state if posts exist
    if (nextIndex === INDEX.EMPTY_STATE && hasAnyCard) {
      nextIndex++;
    }

    // Skip story card if it doesn't exist
    if (nextIndex === INDEX.STORY_CARD && !hasStoryCard) {
      nextIndex++;
    }

    // Skip feed card if it doesn't exist
    if (nextIndex === INDEX.FEED_CARD && !hasFeedCard) {
      nextIndex++;
    }

    // Skip calendar event step if no events exist
    if (nextIndex === INDEX.CALENDAR_EVENT && !hasEvents) {
      nextIndex++;
    }

    return nextIndex;
  };

  // Helper: Find previous valid step index, skipping unavailable targets
  const getPrevValidStepIndex = (currentIndex) => {
    const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
    const { hasAnyCard, hasStoryCard, hasFeedCard } = detectAvailablePosts();
    const { hasEvents } = detectCalendarEvents();

    let prevIndex = currentIndex - 1;

    // Skip calendar event step if no events exist (when going backwards)
    if (prevIndex === INDEX.CALENDAR_EVENT && !hasEvents) {
      prevIndex--;
    }

    // Skip feed card if it doesn't exist (when going backwards)
    if (prevIndex === INDEX.FEED_CARD && !hasFeedCard) {
      prevIndex--;
    }

    // Skip story card if it doesn't exist (when going backwards)
    if (prevIndex === INDEX.STORY_CARD && !hasStoryCard) {
      prevIndex--;
    }

    // Skip empty state if posts exist (when going backwards)
    if (prevIndex === INDEX.EMPTY_STATE && hasAnyCard) {
      prevIndex--;
    }

    return prevIndex;
  };

  // Handler: Before step shows
  const handleStepBefore = useCallback(
    (step, index) => {
      const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
      const { hasAnyCard, hasStoryCard, hasFeedCard } = detectAvailablePosts();
      const { hasEvents } = detectCalendarEvents();

      // Skip empty state step if any posts exist
      if (index === INDEX.EMPTY_STATE) {
        if (hasAnyCard) {
          // Determine which card to jump to
          if (hasStoryCard) {
            setStepIndex(INDEX.STORY_CARD); // Skip to story card
          } else if (hasFeedCard) {
            setStepIndex(INDEX.FEED_CARD); // Skip to feed card
          }
          return;
        }
        // No posts - show empty state as normal
      }

      // Skip story card step if it doesn't exist
      if (index === INDEX.STORY_CARD && !hasStoryCard) {
        if (hasFeedCard) {
          setStepIndex(INDEX.FEED_CARD); // Skip to feed card
        } else {
          setStepIndex(index + 1); // Skip to next step after feed
        }
        return;
      }

      // Skip feed card step if it doesn't exist
      if (index === INDEX.FEED_CARD && !hasFeedCard) {
        if (hasStoryCard) {
          // This shouldn't happen (we'd hit story first), but handle it
          setStepIndex(index + 1);
        } else {
          setStepIndex(index + 1); // Skip to modal steps
        }
        return;
      }

      // Skip calendar event step if no events exist
      if (index === INDEX.CALENDAR_EVENT && !hasEvents) {
        setStepIndex(index + 1); // Skip to calendar change view
        return;
      }

      removeScale();

      const element = document.querySelector(step?.target || '');
      if (!element) return;

      applyScaleToElement(element, index);
      updateSidebarForStep(index);
    },
    [isMobile, isSmallMobile, isMobileView, sidebarOpen, setStepIndex]
  );

  // Handler: Tour finished or skipped
  const handleTourEnd = useCallback(() => {
    removeScale();
    completeTour('default');
    if (sidebarOpen) setSidebarOpen(false);
    if (isPostModalOpen) closeModal();
    if (location.pathname !== '/dashboard') navigate('/dashboard');
  }, [isPostModalOpen, location.pathname, sidebarOpen]);

  // Handler: Navigate to next step
  const handleNextStep = useCallback(
    (index) => {
      const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;
      const { hasStoryCard, hasFeedCard, hasAnyCard } = detectAvailablePosts();

      // Handle empty state - skip directly to socials when no posts exist
      if (index === INDEX.EMPTY_STATE && !hasAnyCard) {
        return delayedStepJump(
          INDEX.SOCIAL_ACCOUNTS,
          () => {
            navigate('/socials');
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
        );
      }

      // Open post modal from story card (when feed card doesn't exist)
      if (index === INDEX.STORY_CARD && !isPostModalOpen && hasStoryCard && !hasFeedCard) {
        const storyCard = document.querySelector('.tour-card-for-story button');
        if (storyCard) {
          return delayedStepTransition(index, () => storyCard.click());
        }
      }

      // Open post modal from feed card (or story card if feed doesn't exist)
      if (index === INDEX.FEED_CARD && !isPostModalOpen && hasAnyCard) {
        // Try feed card first

        if (hasFeedCard) {
          const feedCard = document.querySelector('.tour-card-for-feed button');
          if (feedCard) {
            return delayedStepTransition(index, () => feedCard.click());
          }
        }

        if (hasStoryCard) {
          // Fallback to story card if feed doesn't exist
          const storyCard = document.querySelector('.tour-card-for-story button');
          if (storyCard && hasStoryCard) {
            return delayedStepTransition(index, () => storyCard.click());
          }
        }

        // No posts available - Joyride will skip modal steps automatically
        return false;
      }

      // Navigate to socials after modal (mobile goes directly, desktop has more modal steps)
      if (isMobileView && index === INDEX.POST_MODAL_START) {
        return delayedStepTransition(
          index,
          () => {
            closeModal();
            navigate('/socials');
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
        );
      }

      // Desktop: Navigate to socials after all modal steps
      if (!isMobileView && index === INDEX.POST_MODAL_END) {
        return delayedStepTransition(
          index,
          () => {
            closeModal();
            navigate('/socials');
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
        );
      }

      // Navigate to gallery with sidebar open
      if (index === INDEX.SOCIAL_ACCOUNTS) {
        return delayedStepTransition(
          index,
          () => {
            navigate('/gallery');
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN
        );
      }

      // Navigate to calendar with sidebar open
      if (index === INDEX.CONTENT_GALLERY) {
        return delayedStepTransition(
          index,
          () => {
            navigate('/schedule');
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN
        );
      }

      // Close sidebar for calendar event
      if (index === INDEX.CALENDAR) {
        return delayedStepTransition(index, () => setSidebarOpen(false), DELAYS.SIDEBAR_OPEN);
      }

      // Navigate to profile with sidebar open
      if (index === INDEX.CALENDAR_CHANGE_VIEW) {
        return delayedStepTransition(
          index,
          () => {
            navigate('/profile');
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
        );
      }

      // Close sidebar for profile summary
      if (index === INDEX.PROFILE_START) {
        return delayedStepTransition(index, () => setSidebarOpen(false), DELAYS.SIDEBAR_OPEN);
      }

      // Switch to offerings tab on mobile
      if (index === INDEX.PROFILE_SUMMERY_EDIT && isMobileView) {
        const offeringsTab = document.querySelector("button[data-tab-btn='offerings']");
        if (offeringsTab) {
          return delayedStepTransition(index, () => offeringsTab.click(), DELAYS.SIDEBAR_OPEN);
        }
      }

      // Open sidebar after profile section
      if (index === INDEX.PROFILE_OFFERINGS_EDIT) {
        return delayedStepTransition(
          index,
          () => {
            setTimeout(() => setSidebarOpen(true), DELAYS.MODAL_OPEN);
          },
          DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
        );
      }

      // Default: just advance to next valid step
      const nextValidIndex = getNextValidStepIndex(index);
      updateSidebarForStep(nextValidIndex);
      setStepIndex(nextValidIndex);
      return false;
    },
    [isPostModalOpen, isMobileView, isMobile, isSmallMobile, sidebarOpen]
  );

  // Handler: Navigate to previous step
  const handlePrevStep = useCallback(
    (index) => {
      const INDEX = isMobileView ? STEP_INDEX.MOBILE : STEP_INDEX.DESKTOP;

      // Close post modal when going back from preview to the appropriate card
      if (index === INDEX.POST_MODAL_PREVIEW && isPostModalOpen) {
        closeModal();
        const prevValidIndex = getPrevValidStepIndex(index);
        setTimeout(() => setStepIndex(prevValidIndex), DELAYS.MODAL_OPEN);
        return true;
      }

      // Navigate back when going back from story card
      if (index === INDEX.STORY_CARD) {
        const { hasAnyCard } = detectAvailablePosts();
        // If posts exist, skip empty state and go directly to dashboard
        // If no posts, go to empty state
        const targetIndex = hasAnyCard ? INDEX.DASHBOARD : INDEX.EMPTY_STATE;

        if (isMobileView) {
          setSidebarOpen(true);
          setTimeout(() => setStepIndex(targetIndex), DELAYS.SIDEBAR_OPEN);
        } else {
          setStepIndex(targetIndex);
        }
        return true;
      }

      // Navigate back to socials when going back from gallery
      if (index === INDEX.CONTENT_GALLERY && location.pathname === '/gallery') {
        setRunAndStep(false, index - 1); // Stop tour and set target step
        navigate('/socials');
        setTimeout(() => {
          setSidebarOpen(true);
          setTimeout(() => setRunAndStep(true, index - 1), DELAYS.SIDEBAR_OPEN);
        }, DELAYS.NAVIGATION);
        return true;
      }

      // Navigate back to gallery when going back from calendar
      if (index === INDEX.CALENDAR && location.pathname === '/schedule') {
        setRunAndStep(false, index - 1); // Stop tour and set target step
        navigate('/gallery');
        setTimeout(() => {
          setSidebarOpen(true);
          setTimeout(() => setRunAndStep(true, index - 1), DELAYS.SIDEBAR_OPEN);
        }, DELAYS.NAVIGATION);
        return true;
      }

      // Open sidebar when going back to calendar from event
      if (index === INDEX.CALENDAR_EVENT) {
        setSidebarOpen(true);
        setTimeout(() => setStepIndex(index - 1), DELAYS.SIDEBAR_OPEN);
        return true;
      }

      // Navigate back to schedule when going back from profile
      if (index === INDEX.PROFILE_START && location.pathname === '/profile') {
        setRunAndStep(false, index - 1); // Stop tour and set target step
        navigate('/schedule');
        setTimeout(() => {
          setSidebarOpen(false);
          setTimeout(() => setRunAndStep(true, index - 1), DELAYS.SIDEBAR_OPEN);
        }, DELAYS.NAVIGATION);
        return true;
      }

      // Open sidebar when going back from profile summary
      if (index === INDEX.PROFILE_SUMMERY) {
        setSidebarOpen(true);
        setTimeout(() => setStepIndex(index - 1), DELAYS.SIDEBAR_OPEN);
        return true;
      }

      // Switch back to summary tab on mobile when going back from offerings
      if (index === INDEX.PROFILE_OFFERINGS && isMobileView) {
        const summaryTab = document.querySelector("button[data-tab-btn='summary']");
        if (summaryTab) {
          summaryTab.click();
          setTimeout(() => setStepIndex(index - 1), DELAYS.SIDEBAR_OPEN);
          return true;
        }
      }

      // Close sidebar when going back to profile offerings edit
      if (index === INDEX.SETTINGS) {
        setTimeout(() => {
          setSidebarOpen(false);
          setTimeout(() => setStepIndex(index - 1), DELAYS.SIDEBAR_OPEN);
        }, DELAYS.MODAL_OPEN);
        return true;
      }

      // Navigate back to dashboard and reopen modal when going back from socials
      if (index === INDEX.SOCIAL_ACCOUNTS && location.pathname === '/socials') {
        navigate('/dashboard');
        if (sidebarOpen && isMobileView) setSidebarOpen(false);

        // Use shorter initial delay, then poll for dashboard to be ready
        setTimeout(async () => {
          // Wait for dashboard to fully load before detecting posts
          const { hasAnyCard } = await waitForDashboardLoad();

          // If no posts exist, go back to empty state (don't try to open modal)
          if (!hasAnyCard) {
            if (isMobileView) setSidebarOpen(false);
            setRunAndStep(false, INDEX.EMPTY_STATE);
            setTimeout(() => setRunAndStep(true, INDEX.EMPTY_STATE), 100);
            return;
          }

          // If posts exist, calculate correct target step (last modal step before socials)
          const targetStep = isMobileView ? INDEX.POST_MODAL_START : INDEX.POST_MODAL_END;

          // Reopen modal with feed or story card
          const feedCard = document.querySelector('.tour-card-for-feed button');
          const storyCard = document.querySelector('.tour-card-for-story button');
          const cardToClick = feedCard || storyCard;

          if (cardToClick) {
            setRunAndStep(false, targetStep);
            cardToClick.click();
            // Wait longer for modal to open and render before resuming tour
            setTimeout(
              () => setRunAndStep(true, targetStep),
              DELAYS.MODAL_OPEN + DELAYS.SIDEBAR_OPEN
            );
          }
        }, 100);
        return true;
      }

      // Default: go back to previous valid step
      const prevValidIndex = getPrevValidStepIndex(index);
      updateSidebarForStep(prevValidIndex);
      setStepIndex(prevValidIndex);
      return false;
    },
    [isMobileView, isPostModalOpen, location.pathname, sidebarOpen, isMobile, isSmallMobile]
  );

  // Handler: After step action
  const handleStepAfter = useCallback(
    (action, index) => {
      removeScale();

      switch (action) {
        case ACTIONS.NEXT:
          return handleNextStep(index);

        case ACTIONS.PREV:
          return handlePrevStep(index);

        default:
          return false;
      }
    },
    [handleNextStep, handlePrevStep]
  );

  // Main Joyride callback
  const handleJoyrideCallback = useCallback(
    (data) => {
      const { action, status, type, index, step } = data;

      // Handle tour end states
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        handleTourEnd();
        return;
      }

      // Handle step events
      switch (type) {
        case EVENTS.STEP_BEFORE:
          handleStepBefore(step, index);
          break;

        case EVENTS.STEP_AFTER:
          handleStepAfter(action, index);
          break;

        case EVENTS.TARGET_NOT_FOUND:
          // When a target isn't found, Joyride automatically skips to the next step
          // This happens when post cards don't exist on dashboard
          console.log('Tour step target not found, skipping to next step:', index);
          break;

        case EVENTS.TOUR_END:
          handleTourEnd();
          break;

        default:
          // Ignore other events
          break;
      }
    },
    [handleTourEnd, handleStepBefore, handleStepAfter]
  );

  return {
    steps,
    run,
    stepIndex,
    startTour,
    handleJoyrideCallback,
    tourKey,
    isMobile,
    isTablet,
  };
};

export default useAppTour;
